import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Shield, Building, Zap, Users, FileText, Clock } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const PropertyInsurance = () => {
  const pageData = {
    // SEO Meta Data
    title: "Property Insurance UAE | Business Property Coverage | Yalla Compare",
    metaDescription: "Protect your business property in the UAE with comprehensive property insurance. Get quotes for building, contents, and equipment coverage. Compare rates from top insurers.",

    // Hero Section
    heroTitle: "Comprehensive Property Insurance for UAE Businesses",
    heroSubtitle: "Property Protection",
    heroDescription: "Safeguard your business premises, equipment, and inventory with our comprehensive property insurance solutions. From fire and theft to natural disasters, we've got you covered.",
    heroImage: getImageUrl("par-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Complete building and contents coverage",
      "Protection against fire, theft, and natural disasters",
      "Business interruption coverage available",
      "Flexible coverage limits to suit your needs",
      "24/7 claims support and fast settlements",
      "Competitive premiums from leading UAE insurers"
    ],

    // Features Section
    features: [
      {
        title: "Building Coverage",
        description: "Comprehensive protection for your business premises including structural damage, fixtures, and fittings.",
        icon: <Building className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Contents Protection",
        description: "Coverage for office equipment, inventory, furniture, and other business contents against various risks.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Equipment Insurance",
        description: "Specialized coverage for machinery, computers, and technical equipment essential to your operations.",
        icon: <Zap className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Business Interruption",
        description: "Financial protection when your business operations are disrupted due to covered property damage.",
        icon: <Clock className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Legal Compliance",
        description: "Meet UAE regulatory requirements and protect against legal liabilities related to property damage.",
        icon: <FileText className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Expert Support",
        description: "Dedicated claims team and risk management consultants to help minimize your exposure.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Standard Property Coverage",
        description: "Essential protection for your business property and assets",
        included: [
          "Fire and explosion damage",
          "Theft and burglary protection",
          "Water damage from burst pipes",
          "Storm and flood coverage",
          "Vandalism and malicious damage",
          "Glass breakage protection",
          "Electrical equipment damage",
          "Temporary accommodation costs"
        ]
      },
      {
        title: "Enhanced Coverage Options",
        description: "Additional protection for comprehensive security",
        included: [
          "Business interruption insurance",
          "Loss of rent coverage",
          "Increased cost of working",
          "Professional fees coverage",
          "Debris removal costs",
          "Alternative accommodation",
          "Loss of license coverage",
          "Terrorism and political risks"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "UAE Expertise",
        description: "Deep understanding of local property risks and regulatory requirements in the Emirates."
      },
      {
        title: "Trusted Partners",
        description: "Work with leading insurance providers offering competitive rates and reliable coverage."
      },
      {
        title: "Fast Processing",
        description: "Quick quote generation and policy issuance to get your business protected immediately."
      },
      {
        title: "Ongoing Support",
        description: "Dedicated account management and claims support throughout your policy term."
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "What types of property are covered under business property insurance?",
        answer: "Our property insurance covers commercial buildings, office contents, inventory, equipment, fixtures, and fittings. This includes protection for owned or leased premises in the UAE."
      },
      {
        question: "How is the sum insured determined for property coverage?",
        answer: "The sum insured is based on the replacement value of your property and contents. We help you assess the correct coverage amount through professional valuation if needed."
      },
      {
        question: "Are natural disasters covered under property insurance?",
        answer: "Yes, our policies typically cover natural disasters including floods, storms, and earthquakes. Specific coverage may vary by insurer and location."
      },
      {
        question: "How quickly can claims be processed?",
        answer: "Most property claims are processed within 7-14 days once all documentation is submitted. Emergency repairs can often be authorized within 24-48 hours."
      }
    ],

    // CTA Section
    ctaTitle: "Protect Your Business Property Today",
    ctaDescription: "Get a customized property insurance quote in minutes. Compare coverage options and find the best protection for your UAE business.",
    primaryCta: "Get Property Insurance Quote",
    secondaryCta: "Compare All Insurance Products",

    // Product ID for quote form
    productId: "property-all-risks"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default PropertyInsurance;
